// About/About.js — "/about".
// Carries the .home-page class so the type scale, palette and primitives all
// come from homePage.css, exactly as the home, student and career centre
// pages do. about.css only adds what this page needs on top.
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin } from "lucide-react";

import { FadeIn, FadeInView } from "../ui/fade-in";
import Seo from "../../seo/Seo";

import "../HomePage/homePage.css";
import "./about.css";

import founder1 from "../../assets/founder1.webp";
import founder2 from "../../assets/founder2.webp";
import founder3 from "../../assets/founder3.webp";

const SIGNUP_URL = "https://app.raincareers.com/auth?mode=signUp";

/* `title` is the functional half of the role ("Co-founder, product") and
   `school` the class line ("Hamilton '24"). Both are still blank: nothing in
   this repo records who does what or where any of the three went, and an
   about page is the last place to guess. Fill them in and the line below
   assembles itself — "Co-founder" alone renders cleanly until then, with no
   stray comma or separator left behind. */
const founders = [
  {
    name: "Jacob Gliedman",
    title: "",
    school: "",
    image: founder3,
    linkedin: "https://www.linkedin.com/in/jacob-gliedman-8804291b0/",
  },
  {
    name: "Elliot Carlson",
    title: "",
    school: "",
    image: founder2,
    linkedin: "https://www.linkedin.com/in/elliotcarlson1/",
  },
  {
    name: "Daniel Gliedman",
    title: "",
    school: "",
    image: founder1,
    linkedin: "https://www.linkedin.com/in/daniel-gliedman-256409142/",
  },
];

const roleLine = ({ title, school }) =>
  ["Co-founder" + (title ? `, ${title}` : ""), school]
    .filter(Boolean)
    .join(" · ");

const About = () => (
  <div className="home-page about-page">
    <Seo path="/about" />
    {/* Copy left, the two of us right — the same shape as the career centre
        and student heroes. */}
    <section className="ab-section ab-hero-section">
      <div className="home-container ab-hero">
        <div className="ab-hero-copy">
          <FadeIn delay={0}>
            <span className="ab-eyebrow">About RAIN</span>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="ab-hero-title">Meet the founders</h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            {/* The Slack clause is the founders' own claim about how support
                actually runs — the career centre page promises a dedicated
                channel answered by real people, and this says who they are.
                Cut the clause if that ever stops being the three of them. */}
            <p className="ab-hero-body">
              RAIN was built by three recent grads who went through the job
              search themselves and set out to fix it. Today it runs at 60+
              career centers, and the three of us still answer the Slack
              channel.
            </p>
          </FadeIn>
        </div>

        <FadeIn delay={0.3}>
          <ul className="ab-founders">
            {founders.map((founder) => {
              const { name, image, linkedin } = founder;
              return (
              <li className="ab-founder" key={name}>
                <div className="ab-founder-photo">
                  <img decoding="async" src={image} alt={name} />
                </div>

                <div className="ab-founder-meta">
                  <h2 className="ab-founder-name">{name}</h2>
                  <p className="ab-founder-role">{roleLine(founder)}</p>
                </div>

                <a
                  className="ab-founder-link"
                  href={linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${name} on LinkedIn`}
                >
                  <Linkedin size={17} strokeWidth={1.9} />
                </a>
              </li>
              );
            })}
          </ul>
        </FadeIn>
      </div>
    </section>

    {/* Title left, the story right. */}
    <section className="ab-section ab-vision-section">
      <div className="home-container ab-vision">
        <FadeInView delay={0}>
          <div className="ab-vision-head">
            <span className="ab-eyebrow">Our story</span>
            <h2 className="ab-vision-title">Why we built RAIN</h2>
          </div>
        </FadeInView>

        <FadeInView delay={0.1}>
          <div className="ab-vision-copy">
            <p>
              Finding a job is a messy, stressful process. We&rsquo;ve been
              there: scouring job boards, juggling a dozen browser tabs, and
              wrestling with spreadsheets to keep track of deadlines.
            </p>
            {/* The draft carried a clause here about running a US recruiting
                cycle from six time zones away. It reads well and it may even
                be what happened, but nothing on record says so, and an
                invented detail in a founding story is worse than a plain one.
                Drop it in after "in 2022" once someone confirms it. */}
            <p>
              The idea for RAIN came while we were studying abroad in Sweden in
              2022. After a few hundred conversations with students in
              different fields, the same problems kept coming up: scattered
              tools, no clarity, and the same work over and over.
            </p>
            <p>
              When we started talking to career centers, we heard the other
              side of the same problem. Advisors were pointing students to five
              different tools and couldn&rsquo;t see what happened after the
              appointment ended.
            </p>
            <p>
              So we built one place for the whole search, with a plan inside
              it, for students and for the offices behind them.
            </p>
            <p className="ab-signature">Elliot</p>
          </div>
        </FadeInView>
      </div>
    </section>

    {/* The page closes the way the others do: one light panel, one pill. */}
    <section className="ab-final-section">
      <div className="home-container">
        <FadeInView>
          <div className="ab-final">
            <h2 className="ab-final-title">See RAIN on your campus</h2>

            <p className="ab-final-sub">
              Twenty minutes with one of us, built around your students, your
              staffing, and your calendar.
            </p>

            <Link className="ab-final-btn" to="/career-centers#book">
              Book a walkthrough
            </Link>

            {/* The panel asks career centres for a meeting, so students get
                their own way out of it. "Free" is accurate for anyone: the
                free plan on /pricing is not a trial and not gated on the
                school. */}
            <p className="ab-final-alt">
              Student?{" "}
              <a href={SIGNUP_URL}>
                Sign up free
                <ArrowRight size={15} strokeWidth={2.2} />
              </a>
            </p>
          </div>
        </FadeInView>
      </div>
    </section>
  </div>
);

export default About;
